import { httpClient, qsOptions } from 'providers/clients/httpClient';
import {
  ApiErrorResponse,
  CommonQueryParamsType,
  CommonResultType,
} from 'utility/types/dataProvider';
import qs from 'qs';
import { StatusOnlyResponse } from 'utility/types/dataProvider';
import { paths } from 'utility/types/openapi-ts-generated';

// ------------------------------------------------
// Saved Search / Quick Filters
//------------------------------------------------

// leaving as reference but the type generated is not fully correct
// as it thinks the payload is a Record<string,never>...
// type GeneratedSavedSearchItem = paths['/core/savedsearch/{id}/']['get']['responses']['200']['content']['application/json']['data']

// better type for the saved search item
export type SavedSearchItem = {
  id?: string;
  user_id?: string;
  app?: string;
  name?: string;
  payload?: SaveSearchItemPayload;
  shared?: string | boolean;
  default?: string | boolean;
  created?: string;
  updated?: string;
  [key: string]: any;
};

export type SavedSearchApps = 'editorial' | 'subscription' | 'wire';

export type SavedSearchDefaults = {
  editorial?: string;
  [key: string]: any;
};

export type SaveSearchItemPayload = {
  app?: string[];
  flag?: string[];
  type?: string[];
  draft?: string;
  query?: string;
  /**
   * Deleted filter value. NXT format: '1' = deleted only, '0' = exclude deleted.
   * The `boolean` union member is legacy BLOX admin only (saved searches created
   * in legacy admin store `true`/`false`); normalize via
   * `normalizeLegacyDeletedValue` before consuming.
   */
  deleted?: string | number | boolean;
  section?: string[];
  advertorial?: string;
  photo_sales?: string;
  start_time_to?: string;
  publish_status?: string[];
  author_lastName?: string;
  print_slug_slug?: string;
  start_time_from?: string;
  author_uuid?: string;
  author_email?: string;
  author_phone?: string;
  author_banned?: boolean;
  author_tagline?: string;
  author_byline?: string;
  author_firstName?: string;
  author_screenName?: string;
  author_accountType?: string;
  workflow_process?: string[];
  workflow?: string[];
  [key: string]: any;
};

export type DefaultSavedSearchResult = {
  status?: string | number;
  data?: SavedSearchItem;
  [key: string]: any;
};

// Get /core/savedsearch/search/
// query the saved searches the active user has access to
export type GetSavedSearchesParams = {
  query?: {
    app?: SavedSearchApps[];
    query?: string;
    owner?: string;
    limit?: CommonQueryParamsType['limit'];
    page?: CommonQueryParamsType['page'];
  };
};

// Response get /core/savedsearch/search/
export type GetSavedSearchesResult = {
  status?: CommonResultType['status'];
  data?: {
    total?: CommonResultType['total'];
    next?: CommonResultType['next'];
    prev?: CommonResultType['prev'];
    items?: SavedSearchItem[];
    [key: string]: any;
  };
  [key: string]: any;
};

// GET /core/savedsearch/{id}/
export type GetSavedSearchByIdParams =
  paths['/core/savedsearch/{id}/']['get']['parameters'];

export type GetSavedSearchByIdResult = {
  status?: CommonResultType['status'];
  data?: SavedSearchItem;
  [key: string]: any;
};

// POST (create) /core/savedsearch/
export type CreateSavedSearchParams = {
  body: SavedSearchItem;
};

export type CreateSavedSearchResult = {
  status?: CommonResultType['status'];
  data?: SavedSearchItem;
  [key: string]: any;
};

// PUT (update) /core/savedsearch/{id}/
export type UpdateSavedSearchParams = {
  path?: { id?: string };
  body?: SavedSearchItem;
};

export type UpdateSavedSearchResult = {
  status?: CommonResultType['status'];
  data?: SavedSearchItem;
  [key: string]: any;
};

// DELETE /core/savedsearch/{id}/
export type DeleteSavedSearchParams = {
  path?: { id?: string };
};

// 204 No Content Success
export type DeleteSavedSearchResult = {
  status?: CommonResultType['status'];
  [key: string]: any;
};

// -----------------------------------------------
// -------- SAVED SEARCHES (QUICK FILTERS) -------
// -----------------------------------------------
export const savedSearchesProvider = {
  getDefaultSavedSearch: async (app?: string): Promise<DefaultSavedSearchResult> => {
    try {
      const path = `/core/savedsearch/default/${app}/`;

      const { data, status } = await httpClient({
        url: path,
      });

      const result: DefaultSavedSearchResult = {
        status: data?.status || status,
        data: data?.data,
      };

      // Return the data payload as the default search
      return result;
    } catch (error) {
      const typedError = error as ApiErrorResponse;
      // This endpoint considered 404 as a valid response
      // indicating no default search, override error handling
      // in this case so we can handle error differently in the ui
      if (typedError?.axiosStatus === 404) {
        return {
          status: 404,
          data: {
            payload: undefined,
          },
        };
      } else {
        console.error('[getDefaultSavedSearch] Error:', error);
        throw error;
      }
    }
  },

  setDefaultSavedSearch: async (
    id?: string,
    app?: string,
  ): Promise<StatusOnlyResponse> => {
    try {
      if (!app || !id) {
        throw new Error('[setDefaultSavedSearch] Missing required parameters.');
      }

      const path = `/core/savedsearch/default/${app}/${id}/`;

      const { data } = await httpClient({
        url: path,
        method: 'POST',
      });

      return data;
    } catch (error) {
      console.error('[setDefaultSavedSearch] Error:', error);
      throw error;
    }
  },

  removeDefaultSavedSearch: async (app?: string): Promise<StatusOnlyResponse> => {
    try {
      if (!app) {
        throw new Error('[setDefaultSavedSearch] Missing app parameter.');
      }

      const path = `/core/savedsearch/default/${app}/`;

      const { data } = await httpClient({
        url: path,
        method: 'DELETE',
      });

      return data;
    } catch (error) {
      console.error('[removeDefaultSavedSearch] Error:', error);
      throw error;
    }
  },

  // Get /core/savedsearch/search/ list endpoint
  // query the saved searches the active user has access to
  getSavedSearches: async (
    params: GetSavedSearchesParams,
  ): Promise<GetSavedSearchesResult> => {
    try {
      const basePath = '/core/savedsearch/search/';

      const qp = params?.query ?? {};

      const payload = {
        ...(qp?.app && { app: qp.app }),
        ...(qp?.query && { query: qp.query }),
        ...(qp?.owner && { owner: qp.owner }),
        ...(qp?.page && { page: qp.page }),
        ...(qp?.limit && { limit: qp.limit }),
      };

      const qpStr = Object.keys(payload).length
        ? `?${qs.stringify(payload, qsOptions)}`
        : '';

      const finalPath = `${basePath}${qpStr}`;

      const { data } = await httpClient<GetSavedSearchesResult>({
        url: finalPath,
        method: 'GET',
      });

      const result: GetSavedSearchesResult = {
        status: data?.status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getSavedSearches] Error:', error);
      throw error;
    }
  },

  // get /core/savedsearch/{id}/
  // fetch a saved search by id
  getSavedSearchById: async (
    params: GetSavedSearchByIdParams,
  ): Promise<GetSavedSearchByIdResult> => {
    try {
      const fetchId = params?.path?.id;

      if (!fetchId) {
        throw new Error('[getSavedSearchById] Missing id parameter.');
      }

      const url = `/core/savedsearch/${fetchId}/`;

      const { data } = await httpClient<GetSavedSearchByIdResult>({
        url: url,
        method: 'GET',
      });

      const result: GetSavedSearchByIdResult = {
        status: data?.status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getSavedSearchById] Error:', error);
      throw error;
    }
  },

  // POST (create) /core/savedsearch/
  createSavedSearch: async (
    params: CreateSavedSearchParams,
  ): Promise<CreateSavedSearchResult> => {
    try {
      const body = params?.body;

      if (!body) {
        throw new Error('[createSavedSearch] Missing body parameter.');
      }

      const path = '/core/savedsearch/';

      const { data } = await httpClient<CreateSavedSearchResult>({
        url: path,
        method: 'POST',
        data: JSON.stringify(body),
      });

      const result: CreateSavedSearchResult = {
        status: data?.status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[createSavedSearch] Error:', error);
      throw error;
    }
  },

  // PUT (update) /core/savedsearch/{id}/
  updateSavedSearch: async (
    params: UpdateSavedSearchParams,
  ): Promise<UpdateSavedSearchResult> => {
    try {
      const id = params?.path?.id;
      const body = params?.body;

      if (!id || !body) {
        throw new Error('[updateSavedSearch] Missing required parameters.');
      }

      const path = `/core/savedsearch/${id}/`;

      const { data } = await httpClient<UpdateSavedSearchResult>({
        url: path,
        method: 'PUT',
        data: JSON.stringify(body),
      });

      const result: UpdateSavedSearchResult = {
        status: data?.status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[updateSavedSearch] Error:', error);
      throw error;
    }
  },

  // DELETE /core/savedsearch/{id}/
  deleteSavedSearch: async (
    params: DeleteSavedSearchParams,
  ): Promise<DeleteSavedSearchResult> => {
    try {
      const id = params?.path?.id;

      if (!id) {
        throw new Error('[deleteSavedSearch] Missing required parameters.');
      }

      const path = `/core/savedsearch/${id}/`;

      const { data, status } = await httpClient<DeleteSavedSearchResult>({
        url: path,
        method: 'DELETE',
      });

      const result: DeleteSavedSearchResult = {
        status: data?.status ?? status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[deleteSavedSearch] Error:', error);
      throw error;
    }
  },

  // set as const - all keys (functions) in this object
  // are readonly and we should to ensure they
  // are immutable / cant be accidentally modified
} as const;

// Since all functions have their own signature with types
// we can safely use typeof helper to create the type for us
// and then '&' add it to the BloxCustomDataProvider type
export type SavedSearchesProviderType = typeof savedSearchesProvider;
